import { supabase } from '@/integrations/supabase/client';

export interface SettlementAreas {
  settlement_name: string;
  main_area: string | null;
  sub_area: string | null;           // Backward compatibility
  sub_area_detailed: string | null;
}

// Cache for settlement data
let settlementsCache: SettlementAreas[] = [];
let cacheInitialized = false;

export async function initializeSettlementsCache(): Promise<void> {
  if (cacheInitialized) return;
  
  const { data, error } = await supabase
    .from('israeli_settlements')
    .select('settlement_name, main_area, sub_area, sub_area_detailed');
  
  if (error) {
    console.error('Error fetching settlements:', error);
    return;
  }
  
  settlementsCache = data || [];
  cacheInitialized = true;
}

export function getSettlementAreas(cityName: string): SettlementAreas | null {
  if (!cityName) return null;
  
  const normalizedCity = cityName.trim().toLowerCase();
  
  // Try exact match first
  const exactMatch = settlementsCache.find(s => 
    s.settlement_name.trim().toLowerCase() === normalizedCity
  );
  if (exactMatch) return exactMatch;
  
  // Try partial match
  return settlementsCache.find(s => 
    s.settlement_name.includes(cityName) ||
    cityName.includes(s.settlement_name)
  ) || null;
}

/**
 * Check if a professional serves a given city based on their areas and work_cities
 * 
 * Priority order:
 * 1. work_cities - direct city match
 * 2. main_area - primary area (e.g., "צפון")
 * 3. sub_area_detailed - detailed sub-area (e.g., "אזור חיפה והסביבה")
 * 4. sub_area - backward compatibility (e.g., "חיפה והקריות")
 */
export function professionalServesCity(
  professional: { areas?: string; work_cities?: string[] },
  cityName: string,
  settlementAreas: SettlementAreas | null
): boolean {
  const normalizedCity = cityName.trim().toLowerCase();
  
  // Check 1: Direct city match in work_cities
  if (professional.work_cities && professional.work_cities.length > 0) {
    const matchesWorkCity = professional.work_cities.some(wc => {
      const normalizedWc = wc.trim().toLowerCase();
      return normalizedWc === normalizedCity || 
             normalizedWc.includes(normalizedCity) || 
             normalizedCity.includes(normalizedWc);
    });
    if (matchesWorkCity) return true;
  }
  
  // Check 2-4: Area-based match (main_area, sub_area_detailed, sub_area)
  if (settlementAreas && professional.areas) {
    const profAreas = professional.areas.toLowerCase();
    
    // Check 2: Match against main_area (primary)
    if (settlementAreas.main_area) {
      const mainArea = settlementAreas.main_area.toLowerCase();
      if (profAreas.includes(mainArea)) {
        return true;
      }
    }
    
    // Check 3: Match against sub_area_detailed (primary)
    if (settlementAreas.sub_area_detailed) {
      const subAreaDetailed = settlementAreas.sub_area_detailed.toLowerCase();
      if (profAreas.includes(subAreaDetailed)) {
        return true;
      }
    }
    
    // Check 4: Match against sub_area (backward compatibility)
    if (settlementAreas.sub_area) {
      const subArea = settlementAreas.sub_area.toLowerCase();
      if (profAreas.includes(subArea)) {
        return true;
      }
    }
  }
  
  return false;
}

// Check if cache is ready
export function isSettlementsCacheReady(): boolean {
  return cacheInitialized;
}
